import React from "react";
import { Box, Typography } from "@mui/material";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import WebAssetIcon from "@mui/icons-material/WebAsset";
import TtyIcon from "@mui/icons-material/Tty";
import GridViewIcon from "@mui/icons-material/GridView";
import PersonIcon from "@mui/icons-material/Person";
import { FONT, R, labelSx, thinScrollSx } from "./roomTheme";

/*
 * Bid history list.
 *
 * The row rules are the SAME as before (moved here from rightLayout.jsx unchanged):
 *  - your bid is "winning" (green) when val.userId === userId && win_status === "WIN"
 *    && hammer === highestNumber; it is red when it is yours and LOSS, or WIN but no
 *    longer the highest; label "Win" when status is SOLD and winning, else "Winning" /
 *    "Out Bid";
 *  - other bids show the bidder type (Online Bid, Hall Bid, Absentee Bid, Online,
 *    Phone Bid, Floor Bid); admins also see the bidder's email.
 */

const BIDDER_TYPES = {
  "LIVE BIDDER": ["Online Bid", OnlinePredictionIcon],
  "HALL BIDDER": ["Hall Bid", AccountBalanceIcon],
  "ABSENTEE BIDDER": ["Absentee Bid", WebAssetIcon],
  "INTERNET BIDDER": ["Online", OnlinePredictionIcon],
  "TELEPHONE BIDDER": ["Phone Bid", TtyIcon],
  "FLOOR BIDDER": ["Floor Bid", GridViewIcon],
};

function Row({ icon: Icon, iconColor, label, sublabel, amount, amountColor, strong, mine, compact = false }) {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: compact ? 0.65 : 1,
        py: compact ? 0.4 : 0.75,
        px: 0.5,
        borderBottom: `1px solid ${R.line}`,
        bgcolor: mine ? R.soft : "transparent",
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: compact ? 22 : 26,
          height: compact ? 22 : 26,
          flexShrink: 0,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: mine ? iconColor : "#F0F0F0",
          color: mine ? "#fff" : "#555",
        }}
      >
        <Icon sx={{ fontSize: compact ? 13 : 15 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ ...labelSx, fontSize: compact ? "0.62rem" : undefined, color: mine ? amountColor : R.secondary }}>{label}</Typography>
        {sublabel && (
          <Typography sx={{ fontFamily: FONT, fontSize: compact ? "0.65rem" : "0.75rem", color: R.text, wordBreak: "break-all", lineHeight: 1.3 }}>{sublabel}</Typography>
        )}
      </Box>
      <Typography
        sx={{ fontFamily: FONT, fontWeight: 700, fontSize: compact ? (strong ? "0.8rem" : "0.74rem") : (strong ? "0.9375rem" : "0.875rem"), color: amountColor, whiteSpace: "nowrap" }}
      >
        {"₱ " + amount}
      </Typography>
    </Box>
  );
}

/**
 * `fill` = take the remaining height of a flex column and scroll inside it (desktop panel),
 * so a long history never pushes the bid action out of view. Otherwise `maxHeight` applies.
 */
export default function BidHistory({ bidhistory, userId, highestNumber, status, group, limit, maxHeight, fill = false, compact = false }) {
  return (
    <Box sx={fill ? { display: "flex", flexDirection: "column", minHeight: 0, flex: 1 } : undefined}>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", pb: 0.75, borderBottom: `1px solid ${R.ink}`, flexShrink: 0 }}>
        <Typography component="h3" sx={{ ...labelSx, fontSize: compact ? "0.72rem" : undefined, color: R.ink }}>
          Bid History
        </Typography>
        {bidhistory.length > 0 && (
          <Typography sx={{ ...labelSx, fontSize: compact ? "0.62rem" : undefined, fontWeight: 400 }}>{typeof limit === "number" ? Math.min(bidhistory.length, limit) : bidhistory.length} shown</Typography>
        )}
      </Box>

      {bidhistory.length ? (
        <Box
          component="ul"
          sx={{
            listStyle: "none",
            m: 0,
            p: 0,
            ...(fill ? { flex: 1, minHeight: 0, overflowY: "auto" } : { maxHeight, overflowY: maxHeight ? "auto" : "visible" }),
            ...thinScrollSx,
          }}
        >
          {(typeof limit === "number" ? bidhistory.slice(0, limit) : bidhistory).map((val, i) => {
            if (val.userId === userId) {
              const isWinning = val.userId === userId && val.win_status === "WIN" && val.hammer === highestNumber;
              const isLosing =
                val.userId === userId &&
                (val.win_status === "LOSS" || (val.win_status === "WIN" && val.hammer !== highestNumber));
              const StatusColor = isWinning ? R.success : isLosing ? R.danger : R.ink;
              const label = status === "SOLD" && isWinning ? "Win" : isWinning ? "Winning" : "Out Bid";
              return (
                <Row
                  key={i}
                  mine
                  icon={PersonIcon}
                  iconColor={isWinning ? R.success : "#1976d2"}
                  label={`You · ${label}`}
                  amount={val.hammer.toLocaleString()}
                  amountColor={StatusColor}
                  strong={isWinning}
                  compact={compact}
                />
              );
            }

            const type = BIDDER_TYPES[val.bidder_type];
            if (!type) return null; // unknown bidder types were not shown before either
            const [label, Icon] = type;
            return (
              <Row
                key={i}
                icon={Icon}
                label={label}
                sublabel={group === "ADMIN" ? val.email : null}
                amount={val.hammer.toLocaleString()}
                amountColor={R.ink}
                compact={compact}
              />
            );
          })}
        </Box>
      ) : (
        <Typography sx={{ fontFamily: FONT, fontSize: "0.875rem", textAlign: "center", color: R.secondary, py: 2.5 }}>No Bid History</Typography>
      )}
    </Box>
  );
}
